import React, { useEffect, useState } from 'react';
import { siteLogo, SITE_NAME } from '../../config/branding';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getTenantHeaders } from '../../config/tenant';
import { API_BASE_URL } from '../../apiConfig';

interface ContactData {
  id: number;
  whatsApp_Number: string;
}

const Footer: React.FC = () => {
  const [contactData, setContactData] = useState<ContactData | null>(null);
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  useEffect(() => {
    const fetchContactData = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/Contact/getAll`, {
          headers: {
            ...getTenantHeaders(),
          },
        });
        const result = await response.json();

        if (result.success && result.data.length > 0) {
          setContactData(result.data[0]);
        }
      } catch (error) {
        console.error('Error fetching contact data:', error);
      }
    };

    fetchContactData();
  }, []);

  const renderWhatsAppIcon = (number: string | undefined) => {
    const icon = (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
      </svg>
    );

    const baseWrap =
      'inline-flex h-11 w-11 items-center justify-center rounded-xl text-white bg-emerald-900/55 backdrop-blur-md border-2 border-amber-400/50 shadow-lg transition hover:bg-emerald-900/80 hover:border-amber-400 hover:scale-105 active:scale-95';

    if (number) {
      return (
        <a
          href={`https://wa.me/${number}`}
          target="_blank"
          rel="noopener noreferrer"
          className={baseWrap}
          title="واتساب"
          aria-label="تواصل عبر واتساب"
        >
          {icon}
        </a>
      );
    }

    return (
      <span className={`${baseWrap} opacity-40 cursor-not-allowed`} title="واتساب غير متاح" aria-hidden>
        {icon}
      </span>
    );
  };

  return (
    <footer
      className="relative min-h-[280px] w-full overflow-hidden border-t border-emerald-800/30 bg-gradient-to-br from-emerald-950 via-emerald-900 to-slate-950"
      dir="rtl"
    >
      <div
        className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/45 via-emerald-950/20 to-black/25"
        aria-hidden
      />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-14 flex flex-col min-h-[280px]">
        <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-8 items-start">
          <div className="text-center md:text-right space-y-3">
            <Link to="/" className="inline-block">
              <img
                src={siteLogo}
                alt={SITE_NAME}
                className="h-14 sm:h-16 w-auto object-contain mx-auto md:mx-0 md:mr-0 drop-shadow-[0_2px_10px_rgba(0,0,0,0.4)]"
              />
            </Link>
            <p className="font-cairo text-white/80 text-sm leading-relaxed max-w-sm mx-auto md:mx-0 md:mr-0">
              منصة علوم شرعية — هدفنا أن نوصلك لطلب علمٍ راسخٍ ومنهجٍ واضح.
            </p>
            {!isAuthenticated && (
              <div className="pt-2 flex flex-wrap gap-3 justify-center md:justify-start">
                <Link
                  to="/register"
                  className="inline-flex items-center justify-center rounded-xl px-5 py-2.5 text-sm font-bold text-emerald-950 bg-gradient-to-r from-amber-400 via-amber-500 to-yellow-500 shadow-lg shadow-black/30 ring-2 ring-amber-200/50 transition hover:brightness-105 hover:scale-[1.02] active:scale-[0.98] font-cairo"
                >
                  انشئ حساب
                </Link>
                <Link
                  to="/login"
                  className="inline-flex items-center justify-center rounded-xl px-5 py-2.5 text-sm font-bold text-white bg-emerald-900/55 backdrop-blur-md border-2 border-amber-400/70 shadow-lg transition hover:bg-emerald-900/75 hover:scale-[1.02] active:scale-[0.98] font-cairo"
                >
                  تسجيل الدخول
                </Link>
              </div>
            )}
          </div>

          <div className="text-center md:text-center space-y-4">
            <h3 className="font-cairo text-sm font-semibold text-amber-200/95 tracking-wide">روابط سريعة</h3>
            <div className="flex flex-col items-center gap-2.5">
              <Link to="/" className="font-cairo text-sm text-white/90 hover:text-amber-200 transition-colors">
                الرئيسية
              </Link>
              {location.pathname === '/' ? (
                <button
                  type="button"
                  onClick={() => {
                    document.getElementById('courses-section')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="font-cairo text-sm text-white/90 hover:text-amber-200 transition-colors bg-transparent border-0 p-0 cursor-pointer"
                >
                  المراحل الدراسية
                </button>
              ) : (
                <a href="/#courses-section" className="font-cairo text-sm text-white/90 hover:text-amber-200 transition-colors">
                  المراحل الدراسية
                </a>
              )}
            </div>
          </div>

          <div className="text-center md:text-right flex flex-col items-center md:items-end gap-4">
            <h3 className="font-cairo text-sm font-semibold text-amber-200/95">تواصل معنا</h3>
            <div className="flex flex-wrap items-center justify-center gap-3 md:justify-end">
              {renderWhatsAppIcon(contactData?.whatsApp_Number)}
            </div>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-white/15 text-center">
          <p className="font-cairo text-white/55 text-xs sm:text-sm">
            © {new Date().getFullYear()} {SITE_NAME}. جميع الحقوق محفوظة.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
